import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './StreamingSaltShaker.css';

type Point = { x: number; y: number };

type Grain = Point & {
  id: number;
  size: number;
  burstX: number;
  burstY: number;
  dx: number;
  dy: number;
  life: number;
};

type Props = {
  active: boolean;
  value: string;
  children: React.ReactNode;
  className?: string;
};

const endpointFor = (root: HTMLElement): Point | null => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode() as Text | null;
  let endpoint: Point | null = null;

  while (node) {
    const text = node.textContent || '';
    const end = text.search(/\s*$/);
    const parent = node.parentElement;
    if (end > 0 && parent && !parent.closest('[aria-hidden="true"]')) {
      const range = document.createRange();
      range.setStart(node, end - 1);
      range.setEnd(node, end);
      const rect = range.getBoundingClientRect();
      if (rect.width || rect.height) {
        const direction = window.getComputedStyle(parent).direction;
        endpoint = { x: direction === 'rtl' ? rect.left : rect.right, y: rect.top };
      }
    }
    node = walker.nextNode() as Text | null;
  }

  return endpoint;
};

const StreamingSaltShaker: React.FC<Props> = ({ active, value, children, className = '' }) => {
  const sourceRef = useRef<HTMLDivElement>(null);
  const previousValueRef = useRef(value);
  const grainSequenceRef = useRef(0);
  const [point, setPoint] = useState<Point | null>(null);
  const [grains, setGrains] = useState<Grain[]>([]);
  const gradientId = `streaming-salt-metal-${useId().replace(/:/g, '')}`;
  const glassGradientId = `streaming-salt-glass-${useId().replace(/:/g, '')}`;

  const updatePoint = useCallback(() => {
    const next = active && sourceRef.current ? endpointFor(sourceRef.current) : null;
    setPoint(current => {
      if (!next || !current) return next;
      return Math.abs(current.x - next.x) < 0.5 && Math.abs(current.y - next.y) < 0.5 ? current : next;
    });
  }, [active]);

  useLayoutEffect(() => {
    updatePoint();
    const frame = window.requestAnimationFrame(updatePoint);
    return () => window.cancelAnimationFrame(frame);
  }, [updatePoint, value]);

  useEffect(() => {
    if (!active) return;
    const reposition = () => updatePoint();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [active, updatePoint]);

  useEffect(() => {
    const changed = value !== previousValueRef.current;
    previousValueRef.current = value;
    if (!active || !changed || !value || !point) return;

    const burst = Array.from({ length: 8 }, (): Grain => ({
      id: grainSequenceRef.current++,
      x: point.x + 7 + Math.random() * 4,
      y: point.y - 13 + Math.random() * 4,
      size: 1.8 + Math.random() * 1.8,
      burstX: -7 - Math.random() * 10,
      burstY: 3 + Math.random() * 7,
      dx: -13 - Math.random() * 22,
      dy: 23 + Math.random() * 30,
      life: 470 + Math.random() * 330,
    }));
    const ids = new Set(burst.map(grain => grain.id));
    setGrains(current => [...current, ...burst].slice(-72));
    const timeout = window.setTimeout(() => {
      setGrains(current => current.filter(grain => !ids.has(grain.id)));
    }, 850);
    return () => window.clearTimeout(timeout);
  }, [active, point, value]);

  useEffect(() => {
    if (!active) setGrains([]);
  }, [active]);

  return (
    <>
      <div ref={sourceRef} className={`streaming-salt-source ${className}`.trim()}>{children}</div>
      {active && point && createPortal(
        <div className="streaming-salt-overlay" aria-hidden="true">
          {grains.map(grain => (
            <i
              key={grain.id}
              className="streaming-salt-grain"
              style={{
                left: grain.x,
                top: grain.y,
                '--salt-size': `${grain.size}px`,
                '--salt-burst-x': `${grain.burstX}px`,
                '--salt-burst-y': `${grain.burstY}px`,
                '--salt-dx': `${grain.dx}px`,
                '--salt-dy': `${grain.dy}px`,
                '--salt-life': `${grain.life}ms`,
              } as React.CSSProperties}
            />
          ))}
          <svg
            className="streaming-salt-shaker"
            style={{ left: point.x + 4, top: point.y - 52 }}
            viewBox="0 0 48 62"
            role="presentation"
          >
            <defs>
              <linearGradient id={gradientId} x1="0" x2="1">
                <stop offset="0" stopColor="#8e918f" />
                <stop offset=".25" stopColor="#f0f0eb" />
                <stop offset=".53" stopColor="#a3a5a2" />
                <stop offset=".78" stopColor="#f7f5ee" />
                <stop offset="1" stopColor="#777a78" />
              </linearGradient>
              <linearGradient id={glassGradientId} x1="0" x2="1">
                <stop offset="0" stopColor="#dfe7e5" stopOpacity=".72" />
                <stop offset=".48" stopColor="#fff" stopOpacity=".25" />
                <stop offset="1" stopColor="#aab5b3" stopOpacity=".68" />
              </linearGradient>
            </defs>
            <path d="M13 19h22l4 33c.5 4-2 7-6 7H15c-4 0-6.5-3-6-7l4-33Z" fill={`url(#${glassGradientId})`} stroke="#7c8582" strokeWidth="1.2" />
            <path d="M11.5 18h25l-1.2-9c-.3-2-2-3.5-4-3.5H16.7c-2 0-3.7 1.5-4 3.5l-1.2 9Z" fill={`url(#${gradientId})`} stroke="#737674" />
            <path d="M12 17.5h24" stroke="#666b68" />
            <circle cx="18" cy="10.5" r="1.5" fill="#545856" />
            <circle cx="24" cy="8.5" r="1.5" fill="#545856" />
            <circle cx="30" cy="10.5" r="1.5" fill="#545856" />
            <path d="M13 43c7-3 15 4 23 0l1 10c.3 2-1.5 4-4 4H15c-2.5 0-4.3-2-4-4l2-10Z" fill="#f7f3e8" fillOpacity=".82" />
            <path d="M17 23h4l-2 17h-4l2-17Z" fill="#fff" fillOpacity=".38" />
          </svg>
        </div>,
        document.body
      )}
    </>
  );
};

export default StreamingSaltShaker;
